import { Card, CardContent, Typography, Box, LinearProgress } from "@mui/material";
import Grid from "@mui/material/Grid";

import type { CollectionProgressAmount } from "./types";
import {
  dashboardCollectionProgressBarColor,
  formatDashboardCurrencyAmount,
} from "./dashboard-metrics";

export type CollectionProgressProps = {
  collectionPercentage: number;
  amounts: CollectionProgressAmount[];
  title?: string;
  rateLabel?: string;
  /** When true, progress bar value is capped at 100 (FMB). */
  clampPercentage?: boolean;
};

export default function CollectionProgress({
  collectionPercentage,
  amounts,
  title = "Collection Progress",
  rateLabel = "Collection Rate",
  clampPercentage = false,
}: CollectionProgressProps) {
  const barValue = clampPercentage ? Math.min(collectionPercentage, 100) : collectionPercentage;

  return (
    <Card elevation={2}>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          {title}
        </Typography>
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {rateLabel}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {collectionPercentage}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={barValue}
            sx={{
              height: 10,
              borderRadius: 5,
              backgroundColor: "grey.200",
              "& .MuiLinearProgress-bar": {
                borderRadius: 5,
                backgroundColor: dashboardCollectionProgressBarColor(collectionPercentage),
              },
            }}
          />
        </Box>
        <Grid container spacing={2}>
          {amounts.map((item) => (
            <Grid key={item.label} size={6}>
              <Typography variant="caption" color="text.secondary">
                {item.label}
              </Typography>
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, color: item.color ?? "text.primary" }}
              >
                {formatDashboardCurrencyAmount(item.amount)}
              </Typography>
            </Grid>
          ))}
        </Grid>
      </CardContent>
    </Card>
  );
}
